/**
 * Klaviyo Newsletter Subscription Handler
 *
 * Handles AJAX submission of newsletter signup forms to Klaviyo's subscription API.
 * Maintains Axiom brand styling and provides inline success/error notifications.
 *
 * List ID: TbpyV2
 * API Endpoint: https://manage.kmail-lists.com/ajax/subscriptions/subscribe
 *
 * Custom Properties Sent:
 * - $source: "Footer Form"
 * - consent_to_receive_emails: "Yes"
 * - language: Current Shopify locale (e.g., "en", "fr")
 *
 * IMPORTANT: The $fields parameter is required to tell Klaviyo which custom properties
 * to save. Without it, custom properties are ignored.
 *
 * SPAM PROTECTION:
 * 1. Honeypot field - Catches automated bots (80-90% effective)
 * 2. Rate limiting - 1 submission per email per 60 seconds
 * 3. Client-side validation - Email format and required fields
 *
 * ONSITE TRACKING:
 * After successful subscription, klaviyo.identify() is called to link the browser
 * to the subscriber's email. This enables onsite event tracking (Viewed Product,
 * Add to Cart, etc.) to appear on the user's Klaviyo profile.
 */

(function() {
    'use strict';

    // Email validation regex
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Notification auto-dismiss timeout (milliseconds)
    const NOTIFICATION_TIMEOUT = 5000;

    // Rate limiting configuration (milliseconds)
    const RATE_LIMIT_DURATION = 60000; // 60 seconds
    const RATE_LIMIT_STORAGE_KEY = 'klaviyo_newsletter_submissions';

    // Store timeout IDs to clear previous notifications
    const notificationTimeouts = {};

    /**
     * Initialize all Klaviyo newsletter forms on the page
     */
    function initKlaviyoNewsletterForms() {
        const forms = document.querySelectorAll('.klaviyo-newsletter-form');

        forms.forEach(form => {
            form.addEventListener('submit', handleFormSubmit);
        });
    }

    /**
     * Check if email is rate limited
     * @param {string} email - Email address to check
     * @returns {boolean} - True if rate limited, false otherwise
     */
    function isRateLimited(email) {
        try {
            const submissions = JSON.parse(localStorage.getItem(RATE_LIMIT_STORAGE_KEY) || '{}');
            const lastSubmission = submissions[email];

            if (lastSubmission) {
                const timeSinceLastSubmission = Date.now() - lastSubmission;
                return timeSinceLastSubmission < RATE_LIMIT_DURATION;
            }

            return false;
        } catch (error) {
            // If localStorage is not available or corrupted, allow submission
            console.warn('Rate limiting check failed:', error);
            return false;
        }
    }

    /**
     * Record email submission for rate limiting
     * @param {string} email - Email address to record
     */
    function recordSubmission(email) {
        try {
            const submissions = JSON.parse(localStorage.getItem(RATE_LIMIT_STORAGE_KEY) || '{}');
            submissions[email] = Date.now();

            // Clean up old entries (older than rate limit duration)
            const now = Date.now();
            Object.keys(submissions).forEach(key => {
                if (now - submissions[key] > RATE_LIMIT_DURATION) {
                    delete submissions[key];
                }
            });

            localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify(submissions));
        } catch (error) {
            // If localStorage is not available, continue without rate limiting
            console.warn('Failed to record submission:', error);
        }
    }

    /**
     * Get remaining time for rate limit (in seconds)
     * @param {string} email - Email address to check
     * @returns {number} - Remaining seconds until can submit again
     */
    function getRateLimitRemaining(email) {
        try {
            const submissions = JSON.parse(localStorage.getItem(RATE_LIMIT_STORAGE_KEY) || '{}');
            const lastSubmission = submissions[email];

            if (lastSubmission) {
                const timeSinceLastSubmission = Date.now() - lastSubmission;
                const remainingMs = RATE_LIMIT_DURATION - timeSinceLastSubmission;
                return Math.ceil(remainingMs / 1000);
            }

            return 0;
        } catch (error) {
            return 0;
        }
    }

    /**
     * Handle form submission
     * @param {Event} event - Form submit event
     */
    function handleFormSubmit(event) {
        event.preventDefault();

        const form = event.target;
        const emailInput = form.querySelector('input[name="email"]');
        const consentCheckbox = form.querySelector('input[name="consent"]');
        const honeypotField = form.querySelector('input[name="website"]');
        const listId = form.dataset.listId;
        const sectionId = form.dataset.sectionId;

        // Validate required form elements exist
        if (!emailInput || !consentCheckbox) {
            console.error('Klaviyo form: Required form elements not found');
            return;
        }

        // Validate required data attributes exist
        if (!listId || !sectionId) {
            console.error('Klaviyo form: Missing required data attributes (listId or sectionId)');
            return;
        }

        // Get translated messages from data attributes
        const messages = {
            success: form.dataset.successMessage,
            errorEmailRequired: form.dataset.errorEmailRequired,
            errorEmailInvalid: form.dataset.errorEmailInvalid,
            errorRateLimit: form.dataset.errorRateLimit,
            errorConsentRequired: form.dataset.errorConsentRequired,
            errorUnknown: form.dataset.errorUnknown,
            errorNetwork: form.dataset.errorNetwork
        };

        // SPAM PROTECTION 1: Honeypot check
        if (honeypotField && honeypotField.value !== '') {
            // Bot detected - honeypot was filled
            console.warn('Klaviyo form: Bot detected via honeypot');
            // Silently reject (don't show error to bot)
            showNotification(sectionId, messages.success, 'success');
            emailInput.value = '';
            consentCheckbox.checked = false;
            return;
        }

        // Validate email
        const email = emailInput.value.trim();
        if (!email) {
            showNotification(sectionId, messages.errorEmailRequired, 'error');
            return;
        }

        if (!EMAIL_REGEX.test(email)) {
            showNotification(sectionId, messages.errorEmailInvalid, 'error');
            return;
        }

        // SPAM PROTECTION 2: Rate limiting check
        if (isRateLimited(email)) {
            const remainingSeconds = getRateLimitRemaining(email);
            // Replace {{ seconds }} placeholder with actual value
            const rateLimitMessage = messages.errorRateLimit.replace('{{ seconds }}', remainingSeconds);
            showNotification(sectionId, rateLimitMessage, 'error');
            return;
        }

        // Validate consent checkbox
        if (!consentCheckbox.checked) {
            showNotification(sectionId, messages.errorConsentRequired, 'error');
            return;
        }

        // Show loading state
        setLoadingState(form, true);

        // Get language from form data attribute
        const language = form.dataset.language || 'en';

        // Prepare form data
        const formData = new URLSearchParams();
        formData.append('g', listId);
        formData.append('email', email);

        // IMPORTANT: $fields parameter tells Klaviyo which custom properties to expect
        formData.append('$fields', '$source,consent_to_receive_emails,language');

        // Custom properties
        formData.append('$source', 'Footer Form');
        formData.append('consent_to_receive_emails', 'Yes');
        formData.append('language', language);

        // Submit to Klaviyo
        fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData.toString()
        })
        .then(response => {
            // Check if response is OK before parsing
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            if (data.success || data.data) {
                // Success - subscription added
                showNotification(sectionId, messages.success, 'success');

                // Identify user for Klaviyo onsite tracking
                // This links the browser to the email for tracking Viewed Product, Add to Cart, etc.
                if (typeof klaviyo !== 'undefined') {
                    klaviyo.identify({
                        'email': email,
                        '$source': 'Footer Form',
                        'consent_to_receive_emails': 'Yes',
                        'language': language
                    });
                }

                // Record submission for rate limiting
                recordSubmission(email);

                // Clear form
                emailInput.value = '';
                consentCheckbox.checked = false;
            } else if (data.errors && data.errors.length > 0) {
                // Error from Klaviyo - sanitize message for XSS protection
                const errorMessage = String(data.errors[0] || messages.errorUnknown).substring(0, 200);
                showNotification(sectionId, errorMessage, 'error');
            } else {
                // Unknown response
                showNotification(sectionId, messages.errorUnknown, 'error');
            }
        })
        .catch(error => {
            // Network, parsing, or HTTP error
            console.error('Klaviyo subscription error:', error);
            showNotification(sectionId, messages.errorNetwork, 'error');
        })
        .finally(() => {
            // Reset loading state
            setLoadingState(form, false);
        });
    }

    /**
     * Set loading state on form
     * @param {HTMLFormElement} form - The form element
     * @param {boolean} isLoading - Whether form is in loading state
     */
    function setLoadingState(form, isLoading) {
        const submitButton = form.querySelector('button[type="submit"]');
        const emailInput = form.querySelector('input[name="email"]');
        const consentCheckbox = form.querySelector('input[name="consent"]');

        // Validate all elements exist before manipulating
        if (!submitButton || !emailInput || !consentCheckbox) {
            console.error('Klaviyo form: Cannot set loading state - form elements not found');
            return;
        }

        const buttonText = submitButton.querySelector('.button-text');
        const spinner = submitButton.querySelector('.spinner');

        if (!buttonText || !spinner) {
            console.error('Klaviyo form: Cannot set loading state - button elements not found');
            return;
        }

        if (isLoading) {
            // Disable form
            submitButton.disabled = true;
            emailInput.disabled = true;
            consentCheckbox.disabled = true;

            // Show spinner
            spinner.classList.remove('hidden');
            buttonText.style.opacity = '0.7';
        } else {
            // Enable form
            submitButton.disabled = false;
            emailInput.disabled = false;
            consentCheckbox.disabled = false;

            // Hide spinner
            spinner.classList.add('hidden');
            buttonText.style.opacity = '1';
        }
    }

    /**
     * Show notification message
     * @param {string} sectionId - Section ID for unique notification area
     * @param {string} message - Message to display
     * @param {string} type - Notification type ('success' or 'error')
     */
    function showNotification(sectionId, message, type) {
        const notification = document.getElementById(`klaviyo-notification-${sectionId}`);

        if (!notification) {
            console.warn(`Klaviyo notification element not found for section: ${sectionId}`);
            return;
        }

        // Clear any existing timeout for this notification to prevent conflicts
        if (notificationTimeouts[sectionId]) {
            clearTimeout(notificationTimeouts[sectionId]);
        }

        // Set message
        notification.textContent = message;

        // Set base classes with animation (absolute positioning, no margin)
        const baseClasses = 'klaviyo-notification absolute left-0 right-0 rounded text-sm transition-all duration-300 ease-in-out transform overflow-hidden';

        // Set styling based on type and show with padding
        if (type === 'success') {
            notification.className = `${baseClasses} bg-green-100 text-green-800 border border-green-300 opacity-100 max-h-20 p-3 translate-y-0`;
        } else {
            notification.className = `${baseClasses} bg-red-100 text-red-800 border border-red-300 opacity-100 max-h-20 p-3 translate-y-0`;
        }

        // Auto-dismiss after timeout with fade-out and collapse animation
        notificationTimeouts[sectionId] = setTimeout(() => {
            notification.classList.remove('opacity-100', 'max-h-20', 'p-3', 'translate-y-0');
            notification.classList.add('opacity-0', 'max-h-0', '-translate-y-2');
            delete notificationTimeouts[sectionId];
        }, NOTIFICATION_TIMEOUT);
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initKlaviyoNewsletterForms);
    } else {
        initKlaviyoNewsletterForms();
    }

    // Re-initialize for theme editor section reloads
    if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', initKlaviyoNewsletterForms);
    }
})();
